/**
 * useKonexState — persistencia de Konex en el flujo de agendamiento.
 *
 * El sistema NO tiene cuentas de paciente: el booking es un flujo de invitado
 * verificado por RUT. Por eso el progreso de Konex vive en `localStorage` del
 * navegador y se pierde al cambiar de dispositivo — está bien que así sea.
 *
 * Spec: docs/spec_konex_guide_booking.md §8.
 * Forward-compatible con Fase B: el shape no cambia, solo el cuerpo de konexBrain.
 */

import { useCallback, useState } from 'react';
import type { BookingStep } from '../BookingWizard';

const KONEX_BOOKING_KEY = 'konex_booking';

export interface KonexBookingState {
  /** Pasos cuyo mensaje ya se mostró al menos una vez → konexBrain devuelve null. */
  seenSteps: BookingStep[];
  /** El usuario pulsó cerrar → no auto-abrir; el avatar/pill persiste y es tappable. */
  dismissed: boolean;
}

const KONEX_BOOKING_DEFAULT: KonexBookingState = { seenSteps: [], dismissed: false };

const VALID_STEPS: readonly BookingStep[] = [
  'verification',
  'service-therapist',
  'calendar',
  'patient-confirmation',
  'existing-patient',
  'success',
];

function isBookingStep(value: unknown): value is BookingStep {
  return typeof value === 'string' && (VALID_STEPS as readonly string[]).includes(value);
}

/** Lectura defensiva: SSR, incógnito, storage deshabilitado, JSON corrupto → default. */
function readKonexState(): KonexBookingState {
  if (typeof window === 'undefined') return { ...KONEX_BOOKING_DEFAULT };
  try {
    const raw = window.localStorage.getItem(KONEX_BOOKING_KEY);
    if (!raw) return { ...KONEX_BOOKING_DEFAULT };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return { ...KONEX_BOOKING_DEFAULT };
    const record = parsed as Record<string, unknown>;
    const seenSteps = Array.isArray(record.seenSteps)
      ? record.seenSteps.filter(isBookingStep)
      : [];
    const dismissed = typeof record.dismissed === 'boolean' ? record.dismissed : false;
    return { seenSteps, dismissed };
  } catch {
    return { ...KONEX_BOOKING_DEFAULT };
  }
}

function writeKonexState(next: KonexBookingState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KONEX_BOOKING_KEY, JSON.stringify(next));
  } catch {
    /* incógnito / quota / storage deshabilitado — es una conveniencia, se ignora en silencio */
  }
}

export interface UseKonexStateResult {
  seenSteps: BookingStep[];
  dismissed: boolean;
  /** Marca un paso como visto (dedupe) y persiste. */
  markSeen: (step: BookingStep) => void;
  /** El usuario cerró la burbuja → dismissed = true, persiste. */
  dismiss: () => void;
}

export function useKonexState(): UseKonexStateResult {
  const [state, setState] = useState<KonexBookingState>(readKonexState);

  const markSeen = useCallback((step: BookingStep) => {
    setState((prev) => {
      if (prev.seenSteps.includes(step)) return prev;
      const next: KonexBookingState = { ...prev, seenSteps: [...prev.seenSteps, step] };
      writeKonexState(next);
      return next;
    });
  }, []);

  const dismiss = useCallback(() => {
    setState((prev) => {
      if (prev.dismissed) return prev;
      const next: KonexBookingState = { ...prev, dismissed: true };
      writeKonexState(next);
      return next;
    });
  }, []);

  return { seenSteps: state.seenSteps, dismissed: state.dismissed, markSeen, dismiss };
}
